import { useEffect, useRef, useState } from "react";

/** Libraries */
import useDebounce from "../../app/hooks/useDebounce";

/** Lib Imports */
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import MolGrid, { Column } from "@moltech/molui/molgrid";
import { saveAs } from "file-saver";
import moment from "moment";

/** Utilility */
import { formatDate } from "../../utils/common/dateFormat";

/** Components */
import Badges from "../../components/common/badges/Badges";
import NoRecordFound from "../../components/common/no-record-found/NoRecordFound";

/** RTK Query */
import {
  usePostFileDataMutation,
  usePostGridDataMutation,
  useLazyGetDataQuery,
  usePostDataMutation,
} from "../../app/services/commonApi";

/** CSS Files */
import "./ActivityLogReport.scss";

import ChartWrapper from "../../components/common/Charts/ChartWrapper";
import {
  getHealthRetentionDays,
  runSystemHealthPurge,
} from "../systemHealth/systemHealthPurge";
import { getSystemHealthSettings } from "../../utils/dev/devApiErrorStore";

const renderStatus = (row) => (
  <Badges
    label={row.applicantStatusName}
    type={row.applicantStatusName}
    color={row.applicantStatusColor}
  />
);

const ActivityLog = () => {
  const candidateReportRef = useRef();
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [gridData, setGridData] = useState([]);
  const [searchApplicantname, setSearchApplicantname] = useState("");
  const [apiMessage, setApiMessage] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const debouncedSearch = useDebounce(searchApplicantname, 300);
  const [fetchGridData] = usePostGridDataMutation();
  const [fetchFileData, { isLoading: isExporting }] = usePostFileDataMutation();
  const [postPurge] = usePostDataMutation();

  const handleClear = () => {
    setSelectedEndDate(null);
    setSelectedStartDate(null);
    setSearchApplicantname("");
    setGridData([]);
    setApiMessage("");
    setHasSearched(false);
    statusDistributionDataReset();
    ActivityTrendDataReset();
  };
  /** RTK Query */
  const [triggerGetStatusDistribution, { data: distributionApiData, reset: statusDistributionDataReset, isLoading: isStatusDataLoading, isFetching: isStatusDataFetching }] = useLazyGetDataQuery();
  const [triggerGetActivityTrend, { data: trendApiData, reset: ActivityTrendDataReset, isLoading: isTrendLoading, isFetching: isTrendFetching }] = useLazyGetDataQuery();

  const getActivityLogList = () => {
    setHasSearched(true);
    const payload = {
      startDate: selectedStartDate
        ? formatDate(selectedStartDate, "YYYY-MM-DD")
        : null,
      endDate: selectedEndDate
        ? formatDate(selectedEndDate, "YYYY-MM-DD")
        : null,
      Name: debouncedSearch?.trim() || null,
    };

    fetchGridData({
      url: "Report/GetActivityLogsByDateRange",
      params: payload,
    }).then((res) => {
      if (res?.data?.success) {
        setGridData(res.data.data ?? []);
        setApiMessage("");
      } else if (res?.data && !res?.data?.success) {
        setGridData([]);
        setApiMessage(
          res.data.message || "No activity logs found for the selected range.",
        );
      } else if (res.error) {
        setGridData([]);
        setApiMessage(
          res.error.data?.message || "Error fetching activity logs.",
        );
      }

      triggerGetStatusDistribution({
        url: "Analytics/GetStatusDistributionChart",
        method: "GET",
        data: {
          startDate: selectedStartDate
            ? moment(selectedStartDate).utc().toISOString()
            : moment.utc("2000-01-01").startOf("day").toISOString(),
          endDate: selectedEndDate
            ? moment(selectedEndDate).utc().toISOString()
            : moment.utc().endOf("day").toISOString(),
        }
      });

      triggerGetActivityTrend({
        url: "Analytics/GetActivityTrendChart",
        method: "GET",
        data: {
          startDate: selectedStartDate
            ? moment(selectedStartDate).utc().toISOString()
            : moment.utc("2000-01-01").startOf("day").toISOString(),
          endDate: selectedEndDate
            ? moment(selectedEndDate).utc().toISOString()
            : moment.utc().endOf("day").toISOString(),
        }
      });
    });
  };

  useEffect(() => {
    if (debouncedSearch?.trim() || hasSearched) {
      getActivityLogList();
    }
    // Intentionally only when debounced search text changes; Search button /
    // date filters call getActivityLogList directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [debouncedSearch]);

  const handleSearch = async () => {
    getActivityLogList();
  };

  const handleDateRangeChange = (e) => {
    const [startDate, endDate] = e.value || [];
    setSelectedStartDate(startDate || null);
    setSelectedEndDate(endDate || null);
  };

  const handleExport = async () => {
    const response = await fetchFileData({
      url: "Report/ExportActivityLog",
      data: {
        startDate: selectedStartDate,
        endDate: selectedEndDate,
      },
    });
    const fileData = response.data;
    const blob = new Blob([fileData]);

    const startDateStr = selectedStartDate
      ? formatDate(selectedStartDate, "DD-MM-YYYY")
      : "";
    const endDateStr = selectedEndDate
      ? formatDate(selectedEndDate, "DD-MM-YYYY")
      : "";

    let filename = "Activity-Log-Report";
    if (startDateStr && endDateStr) {
      filename += `-${startDateStr}-to-${endDateStr}`;
    } else if (startDateStr) {
      filename += `-from-${startDateStr}`;
    } else if (endDateStr) {
      filename += `-to-${endDateStr}`;
    }
    filename += ".xlsx";

    saveAs(blob, fileData.filename || filename);
  };

  const header = (
    <div className="custom-header-content mt-4">
      <div className="p-card-title ">
        Activity Log Report
        <div className="header-items">
          <InputText
            placeholder="Search by candidate name"
            value={searchApplicantname}
            onChange={(e) => {
              const value = e.target.value;
              setSearchApplicantname(value);
            }}
          />
          <Button
            label="Export Report"
            icon="pi pi-download"
            className="p-button-sm export-btn  btn secondary-btn"
            onClick={handleExport}
            disabled={isExporting || !gridData.length}
            loading={isExporting}
          />
        </div>
      </div>
    </div>
  );

  const buildDonutTooltip = () => {
    return function ({ series, seriesIndex, w }) {
      if (!w?.globals) return "";

      const value = series[seriesIndex];
      const totals = w.globals.seriesTotals || [];
      const totalSum = totals.reduce((a, b) => a + b, 0);

      const percent =
        totalSum > 0 ? ((value / totalSum) * 100).toFixed(1) : 0;

      const label = w.globals.labels[seriesIndex];
      const bgColor = w.globals.colors[seriesIndex];

      return `
        <div style="
          padding:8px 12px;
          background:${bgColor};
          color:#fff;
          border-radius:6px;
          font-size:13px;
        ">
          <strong>${label}</strong><br/>
          ${value} (${percent}%)
        </div>
      `;
    };
  };

  return (
    <div className="applicant-report">
      <Card className="custom-card height-full-grid-with-searchbar mb-0 ">
        <div className="page-header">
          <div className="left-title-part">
            <h2 className="card-title">Activity Log Report</h2>
          </div>
          {getSystemHealthSettings().purgeActivityLogsEnabled ? (
            <div className="right-btn-part">
              <Button
                label={`Purge >${getHealthRetentionDays()}d`}
                icon="pi pi-filter-slash"
                className="p-button-sm btn secondary-btn"
                onClick={async () => {
                  await runSystemHealthPurge(postPurge, "activity");
                }}
              />
            </div>
          ) : null}
        </div>
        <div className="card-filter">
          <div className="applicant-filter">
            <div className="filter-fields">
              <Calendar
                name="startDate"
                label="Date Range"
                placeholder="Select Date Range"
                dateFormat="dd-mm-yy"
                selectionMode="range"
                showIcon
                showButtonBar
                value={
                  selectedStartDate || selectedEndDate
                    ? [selectedStartDate, selectedEndDate]
                    : null
                }
                onChange={handleDateRangeChange}
              />
              <div className="btn-group-sec">
                <Button
                  label="Search"
                  icon="pi pi-search"
                  onClick={handleSearch}
                  className="p-button-sm btn secondary-btn "
                />
                <Button
                  label="Clear"
                  icon="pi pi-times"
                  className="p-button-sm p-button-secondary btn secondary-light-btn"
                  onClick={handleClear}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="applicant-report-top-section">
        <div className="chart-row">
          <ChartWrapper
            title="Status Distribution"
            type="donut"
            data={distributionApiData?.data?.statusCounts || []}
            xKey="status"
            yKey="count"
            colorType="status"
            customOptions={{
              tooltip: {
                custom: buildDonutTooltip()
              }
            }}
            centerLabel="Total Logs"
            loading={isStatusDataLoading || isStatusDataFetching}

          />
          <ChartWrapper
            title="Activity Trend"
            type="area"
            data={trendApiData?.data?.activityTrend}
            xKey="label"
            yKey="count"
            customOptions={{
              stroke: { curve: "smooth", width: 3 },
              fill: { type: "solid", opacity: 0.2 },
              markers: { size: 4 },
              xaxis: {
                title: { text: "Time Period" }
              },
              yaxis: {
                title: { text: "Activity Count" }
              }
            }}
            loading={isTrendLoading || isTrendFetching}
          />
        </div>
      </div>

      <Card className="custom-card job-report-card-section" header={header}>
        {gridData?.length ? (
          <MolGrid
            ref={candidateReportRef}
            paginator={false}
            scrollable
            scrollHeight="flex"
            filterDisplay="none"
            value={gridData}
          >
            <Column field="applicantName" header="Candidate Name" hideColumnMenu={true} />
            <Column
              field="activityDate"
              header="Activity Date"
              body={(rowData) => formatDate(rowData.activityDate)}
              hideColumnMenu={true}
            />
            <Column
              field="applicantStatusName"
              header="Status"
              body={renderStatus}
              hideColumnMenu={true}
            />
            <Column field="activity" header="Activity" hideColumnMenu={true} />
            <Column field="comments" header="Comments" sortable={false} hideColumnMenu={true} />
            <Column field="rejectedReasonText" header="Rejected Reason" hideColumnMenu={true} />
            <Column
              field="applyDate"
              header="Applied Date"
              body={(rowData) => formatDate(rowData.applyDate)}
              hideColumnMenu={true}
            />
            <Column field="jobOpeningName" header="Job Opening" hideColumnMenu={true} />
          </MolGrid>
        ) : (
          <NoRecordFound
            message={
              !hasSearched
                ? "Please select a date range and click Search to view activity logs."
                : apiMessage || "No activity logs found for the selected range."
            }
          />
        )}
      </Card>
    </div>
  );
};

export default ActivityLog;
